import React, { useState, useEffect, useContext, useRef, useMemo } from 'react';
import Dropdown from 'react-bootstrap/Dropdown';
import { getWhatsappTemplates } from '../../utils/api/messages';
import { camelCaseToText } from '../../utils/camelCaseToText';
import { UserProfileContext } from '../../context/userProfileContext';
import { useTranslation } from 'react-i18next';

interface ModulesMessageTypes {
    [key: string]: string[];
}

interface TemplatesDropdownProps {
    title: string;
    onTemplateSelected: (messageType: string) => void;
    modulesMessageTypes: ModulesMessageTypes;
}

const TemplatesDropdown = ({ onTemplateSelected, title, modulesMessageTypes }: TemplatesDropdownProps) => {
    const { t } = useTranslation('chat-area');
    // const [templates, setTemplates] = useState<ModulesMessageTypes>({});
    const [groupedTemplates, setGroupedTemplates] = useState<Map<string, string[]>>(new Map());
    const [isDropdownOpen, setIsDropdownOpen] = useState<boolean>(false);
    const { profileContextData } = useContext(UserProfileContext);
    const { client } = profileContextData;
    const [activeModule, setActiveModule] = useState<string | null>(client?.currentModule || null);
    const [searchTerm, setSearchTerm] = useState<string>('');
    const dropdownRef = useRef<HTMLDivElement>(null);
    const searchInputRef = useRef<HTMLInputElement>(null);

    // useEffect(() => {
    //     const fetchTemplates = async () => {
    //         const response = await getWhatsappTemplates(client);
    //         setTemplates(response.modulesMessageTypes);
    //     };
    //     fetchTemplates();
    // }, [client]);

    useEffect(() => {
        const grouped = Object.entries(modulesMessageTypes).reduce((acc, [module, messageTypes]) => {
            messageTypes && messageTypes.length && acc.set(module, messageTypes);
            return acc;
        }, new Map<string, string[]>());

        setGroupedTemplates(grouped);
    }, [modulesMessageTypes]);

    const filteredTemplates = useMemo(() => {
        if (!searchTerm.trim()) {
            return groupedTemplates;
        }

        const lowerSearchTerm = searchTerm.toLowerCase();
        const filtered = new Map<string, string[]>();

        groupedTemplates.forEach((messageTypes, module) => {
            const matchingTemplates = messageTypes.filter((messageType) =>
                camelCaseToText(messageType).toLowerCase().includes(lowerSearchTerm)
            );
            if (matchingTemplates.length > 0) {
                filtered.set(module, matchingTemplates);
            }
        });

        return filtered;
    }, [groupedTemplates, searchTerm]);

    useEffect(() => {
        if (isDropdownOpen && searchInputRef.current) {
            searchInputRef.current.focus();
        }
        if (!isDropdownOpen) {
            setSearchTerm('');
        }
    }, [isDropdownOpen]);

    const handleDropdownToggle = (nextShow: boolean, meta: any) => {
        setIsDropdownOpen(nextShow);
        if (nextShow && dropdownRef.current) {
            const rect = dropdownRef.current.getBoundingClientRect();
            const windowHeight = window.innerHeight;
            const dropdownHeight = 400; // Set a fixed height for the dropdown
            const dropdownBottom = rect.bottom + dropdownHeight;

            if (dropdownBottom > windowHeight) {
                dropdownRef.current.style.transform = `translateY(-${dropdownHeight + 20}px)`;
            } else {
                dropdownRef.current.style.transform = 'translateY(0)';
            }
        }
    };

    const handleModuleToggle = (module: string) => {
        setActiveModule(activeModule === module ? null : module);
    };

    const getHeaderClassName = (index: number, array: Array<[string, string[]]>) => {
        let classNames = 'fixed-width d-flex justify-content-between align-items-center fw-bold fs-5 cursor-pointer text-dark w-100';
        if (index > 0 && array[index - 1][0] === activeModule) {
            classNames += ' border-top-0';
        } else {
            classNames += ' border-top';
        }
        if (index === array.length - 1) {
            classNames += ' border-bottom';
        }
        return classNames;
    };

    return (
        <div className="assign-template">
            <Dropdown className="select-action text-center" align={'end'} onToggle={handleDropdownToggle} show={isDropdownOpen}>
                <Dropdown.Toggle as="a" className="btn-sm card-drop cursor-pointer">
                    <span>
                        {camelCaseToText(title)}
                        <i className="mdi mdi-chevron-down"></i>
                    </span>
                </Dropdown.Toggle>
                <Dropdown.Menu ref={dropdownRef} className="dropdown-menu-scroll">
                    <div className="dropdown-menu-inner">
                        <div className="p-2">
                            <input
                                ref={searchInputRef}
                                type="text"
                                className="form-control"
                                placeholder={t('Search')}
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                onClick={(e) => e.stopPropagation()}
                            />
                        </div>
                        {Array.from(filteredTemplates.entries()).map(([module, messageTypes], index, array) => (
                            <React.Fragment key={module}>
                                <Dropdown.Header className={getHeaderClassName(index, array)} onClick={() => handleModuleToggle(module)}>
                                    {camelCaseToText(module)}
                                    <span className="accordion-icon">{activeModule === module ? '➖' : '➕'}</span>
                                </Dropdown.Header>
                                {activeModule === module &&
                                    messageTypes.map((messageType) => (
                                        <Dropdown.Item key={messageType} onClick={() => onTemplateSelected(messageType)}>
                                            {camelCaseToText(messageType)}
                                        </Dropdown.Item>
                                    ))}
                            </React.Fragment>
                        ))}
                    </div>
                </Dropdown.Menu>
            </Dropdown>
        </div>
    );
};

export default TemplatesDropdown;
